/**
 * THE LITE STORE, FOR A PHONE THAT CANNOT KEEP UP (2026-11-03).
 *
 * The owner, after DK Store's product page and checkout on an old Android phone inside Instagram's browser:
 * "I tested it on old device and the experience was bad.. things are slow.. the animation of opening are slow and
 * also scrolling.. maybe we should be able to identify such devices and lower the transitions and the stuff.. the
 * scrolling (showing hiding whatsapp and hiding showing bottom bar) was making the experience slower.. and the
 * opening of the checkout as well"
 *
 * One decision, made in index.js BEFORE the first render and written on the root as `<html data-lite="1">`, so the
 * very first paint is already the lite one (styles/lite.css). Components read it with `isLite()` / `useLite()`.
 *
 * THE RULE, first answer wins:
 *
 *   1. `?lite=on|off` on any address — the owner's and the test rig's switch — kept in localStorage
 *      (`stylon:lite`) so it holds on the next page; `?lite=auto` forgets it (and a remembered measurement).
 *   2. `prefers-reduced-motion: reduce` → lite. The shopper asked their phone for less motion; lite IS less motion.
 *      (Android's "Remove animations" sets it, and people with slow phones turn that on for speed.)
 *   3. `navigator.connection.saveData` → lite. Chrome's data saver is the shopper asking for a lighter web.
 *   4. `navigator.deviceMemory ≤ 2` → lite on its own. Chrome only, rounded DOWN to a power of two and capped at 8:
 *      a 3 GB phone says 2. Phones that small are the low end, whatever their cores.
 *   5. `deviceMemory ≤ 4` AND `hardwareConcurrency ≤ 4` → lite. Four alone is not enough: Chrome rounds a 6 GB phone
 *      down to 4, and plenty of capable mid-range phones say 4 — but they have eight cores. Four cores or fewer with
 *      that memory is the quad-core budget phone.
 *   6. Slow frames measured on an earlier visit, within the last 30 days → lite (see 'frames' below).
 *   7. Otherwise — and when the phone says nothing at all — NOT lite.
 *
 * WHAT IS DELIBERATELY NOT A SIGNAL:
 *   - `hardwareConcurrency` alone. Safari reports at most a couple of cores on every iPhone (WebKit caps it against
 *     fingerprinting) and no `deviceMemory`, so "few cores" would make every iPhone lite. Cores count only beside a
 *     memory figure, which only Chrome gives.
 *   - `effectiveType` 2g/3g. A slow LINE does not make a FRAME slow; what lite takes away is work on the phone's
 *     processor, not bytes. `saveData` is the shopper's own word for wanting less, so that one counts.
 *
 * FRAMES. Most old Android phones are octa-core with 3–4 GB, so the hardware rules miss many of them. For a phone the
 * rules above left undecided, `watchFramesForLite` counts how long the page's frames stall in the first
 * FRAME_WINDOW_MS after it starts — the boot every phone does on every visit: the app, the product, its photographs.
 * Every frame gap over 50 ms counts by its excess (a gap of 400 ms blocked 350), each capped at 1 s; a second or more
 * of it in the window is a slow phone. Measured on DK's product page (Playwright, 360×800, an M1 Max with its CPU
 * throttled): ×1 ≈ 50 ms blocked, ×4 (a mid-range phone) ≈ 670 ms, ×6 ≈ 1.2–1.4 s, ×16 ≈ 3 s. The phone goes lite
 * there and then — the components re-read — and the verdict is remembered for 30 days so the next visit starts lite
 * from its first paint. A page that is hidden at any point in the window decides nothing (a background page paints
 * no frames, which would read as one enormous stall), and a browser driven by automation is never measured.
 * Checked with the built store (`?lite=auto`, 2 runs each, before the automation rule): ×1 and ×4 stayed full, ×6, ×8
 * and ×12 turned lite.
 *
 * UNDER LITE, the page's own reduced-motion questions are answered "yes": `window.matchMedia` is wrapped for the
 * two `prefers-reduced-motion` queries only (every other query goes to the browser untouched), so the ~20 places that
 * already honour reduced motion in JavaScript — smooth scrolls into view, the checkout's step slide, the chat's travel,
 * the cover's fade on scroll — follow lite without each being edited. A phone that is not lite keeps the browser's
 * own `matchMedia`, unwrapped.
 */
import { useSyncExternalStore } from 'react';

export const LITE_OVERRIDE_KEY = 'stylon:lite';
export const LITE_FRAMES_KEY = 'stylon:lite:frames';
export const FRAMES_REMEMBERED_DAYS = 30;
/** Chrome's `deviceMemory` (GB, rounded down to a power of two): at or under this alone is lite. */
export const LOW_MEMORY_GB = 2;
/** At or under this, with FEW_CORES or fewer, is lite. */
export const MID_MEMORY_GB = 4;
export const FEW_CORES = 4;
/** How long after the page starts its frames are watched. */
export const FRAME_WINDOW_MS = 6000;
/** A frame gap over this is a stall (three frames missed at 60 Hz); it counts by its excess. */
export const LONG_FRAME_MS = 50;
/** No single gap counts for more than this — a paused tab or a dialog is not the phone's speed. */
export const GAP_CAP_MS = 1000;
/** This much stall inside the window is a slow phone. */
export const SLOW_BLOCKED_MS = 1000;

const DAY_MS = 24 * 3600 * 1000;
const REDUCE = '(prefers-reduced-motion:reduce)';
const NO_PREFERENCE = '(prefers-reduced-motion:no-preference)';

const defaultWindow = () => (typeof window !== 'undefined' ? window : undefined);
const defaultStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch (e) { return null; }
};
const read = (storage, key) => { try { return storage ? storage.getItem(key) : null; } catch (e) { return null; } };
const write = (storage, key, value) => { try { if (storage) storage.setItem(key, value); } catch (e) { /* nowhere to keep it */ } };
const forget = (storage, key) => { try { if (storage) storage.removeItem(key); } catch (e) { /* nothing to forget */ } };
/** A positive, finite number, or null: '2', 0 and NaN say nothing. */
const positive = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

/* ── The decision ─────────────────────────────────────────────────────────────────────────────────────────────── */

/** The rule above, on the facts `readLiteSignals` gathers. `{ lite, by }` — `by` names the signal, null for none. */
export function decideLite(signals = {}) {
  const s = signals || {};
  if (s.override === 'on') return { lite: true, by: 'override' };
  if (s.override === 'off') return { lite: false, by: 'override' };
  if (s.reducedMotion === true) return { lite: true, by: 'motion' };
  if (s.saveData === true) return { lite: true, by: 'save-data' };
  const memory = positive(s.deviceMemory);
  const cores = positive(s.cores);
  if (memory != null && memory <= LOW_MEMORY_GB) return { lite: true, by: 'memory' };
  if (memory != null && memory <= MID_MEMORY_GB && cores != null && cores <= FEW_CORES) return { lite: true, by: 'cores' };
  if (s.slowFramesSeen === true) return { lite: true, by: 'frames' };
  return { lite: false, by: null };
}

/** The browser's own matchMedia, even after lite has wrapped it. */
const realMatchMedia = (win) => {
  const mm = win && win.matchMedia;
  if (typeof mm !== 'function') return null;
  return mm.__stylonLiteReal || mm;
};

/** What this phone and this browser say, and what an earlier visit remembered. Never throws. */
export function readLiteSignals({ win = defaultWindow(), storage = defaultStorage(), now = Date.now() } = {}) {
  const nav = (win && win.navigator) || {};
  const override = read(storage, LITE_OVERRIDE_KEY);
  let reducedMotion = false;
  try {
    const mm = realMatchMedia(win);
    reducedMotion = !!(mm && mm.call(win, '(prefers-reduced-motion: reduce)').matches);
  } catch (e) { reducedMotion = false; }
  let saveData = false;
  try { saveData = !!(nav.connection && nav.connection.saveData === true); } catch (e) { saveData = false; }
  const measuredAt = Number(read(storage, LITE_FRAMES_KEY));
  const slowFramesSeen = Number.isFinite(measuredAt) && measuredAt > 0 && now - measuredAt < FRAMES_REMEMBERED_DAYS * DAY_MS;
  return {
    override: override === 'on' || override === 'off' ? override : null,
    reducedMotion,
    saveData,
    deviceMemory: nav.deviceMemory,
    cores: nav.hardwareConcurrency,
    slowFramesSeen,
  };
}

/** `?lite=on|off` is kept for this browser; `?lite=auto` forgets it and any remembered measurement. */
export function applyLiteFromUrl(search = '', storage = defaultStorage()) {
  let value = null;
  try { value = new URLSearchParams(search || '').get('lite'); } catch (e) { value = null; }
  if (value === 'on' || value === 'off') write(storage, LITE_OVERRIDE_KEY, value);
  else if (value === 'auto') { forget(storage, LITE_OVERRIDE_KEY); forget(storage, LITE_FRAMES_KEY); }
}

/* ── The one decision of this page session, and who is told when it changes ──────────────────────────────────── */

let state = { lite: false, by: null };
let stateWin = null;
const listeners = new Set();

/** True when this page is in lite. Before `installLiteMark` ran (a test, a module read early), false. */
export function isLite() { return state.lite; }
/** Which signal decided it: 'override' | 'motion' | 'save-data' | 'memory' | 'cores' | 'frames' | null. */
export function liteReason() { return state.by; }
/** Called whenever lite changes (only ever off → on, once the frames have been measured). Returns the unsubscribe. */
export function subscribeLite(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/** React's reading of it: re-renders the component the moment the phone turns lite. */
export function useLite() {
  return useSyncExternalStore(subscribeLite, isLite, () => false);
}

/** A MediaQueryList that always answers `matches`. */
function fixedQuery(media, matches) {
  return {
    matches, media, onchange: null,
    addEventListener() {}, removeEventListener() {}, addListener() {}, removeListener() {},
    dispatchEvent() { return false; },
  };
}

/** Under lite, the page's reduced-motion questions are answered "yes"; every other question is the browser's. */
function bridgeReducedMotion(win) {
  if (!win || typeof win.matchMedia !== 'function' || win.matchMedia.__stylonLiteReal) return;
  const real = win.matchMedia;
  const bridged = function matchMedia(query) {
    const q = String(query == null ? '' : query).replace(/\s+/g, '').toLowerCase();
    if (state.lite && q === REDUCE) return fixedQuery(query, true);
    if (state.lite && q === NO_PREFERENCE) return fixedQuery(query, false);
    return real.call(win, query);
  };
  bridged.__stylonLiteReal = real;
  win.matchMedia = bridged;
}

function apply(decision, win) {
  state = { lite: !!decision.lite, by: decision.by || null };
  stateWin = win || null;
  const root = win && win.document && win.document.documentElement;
  if (root) {
    if (state.lite) root.setAttribute('data-lite', '1');
    else root.removeAttribute('data-lite');
  }
  if (state.lite) bridgeReducedMotion(win);
  listeners.forEach((fn) => { try { fn(); } catch (e) { /* a listener never breaks the page */ } });
}

/**
 * index.js, before the first render: the address's override, then the decision, then the mark on <html>. Returns
 * the decision. Never throws.
 */
export function installLiteMark({ win = defaultWindow(), storage = defaultStorage(), now = Date.now() } = {}) {
  try {
    applyLiteFromUrl(win && win.location ? win.location.search : '', storage);
    const decision = decideLite(readLiteSignals({ win, storage, now }));
    apply(decision, win);
    return decision;
  } catch (e) {
    return { lite: false, by: null };
  }
}

/* ── Frames ───────────────────────────────────────────────────────────────────────────────────────────────────── */

/** The frames rule: a second or more of stall in the window. */
export function framesSaySlow({ blockedMs } = {}) {
  return typeof blockedMs === 'number' && blockedMs >= SLOW_BLOCKED_MS;
}

/**
 * Watch the first FRAME_WINDOW_MS of frames of a phone nothing else decided, and turn it lite if they stall. Started
 * by index.js right after the mark, so the window holds the boot. Returns a stop function. Never throws.
 */
export function watchFramesForLite({ win = defaultWindow(), storage = defaultStorage(), now = () => Date.now() } = {}) {
  const noop = () => {};
  try {
    if (state.lite || state.by === 'override') return noop;
    if (!win || typeof win.requestAnimationFrame !== 'function' || !win.document) return noop;
    // A browser driven by automation (`navigator.webdriver`: Playwright, a bot) is not a shopper's phone, and a loaded
    // test machine stalls like an old one: measured, it would turn the e2e runs lite at random. `?lite=on` still works.
    if (win.navigator && win.navigator.webdriver === true) return noop;
    const doc = win.document;
    const hidden = () => doc.visibilityState === 'hidden';
    let stopped = false;
    let started = false;
    let first = null;
    let last = null;
    let blockedMs = 0;
    const stop = () => {
      stopped = true;
      try { doc.removeEventListener('visibilitychange', onVisibility); } catch (e) { /* gone */ }
    };
    const tick = (t) => {
      if (stopped) return;
      if (first == null) { first = t; last = t; }
      else {
        const gap = t - last;
        last = t;
        if (gap > LONG_FRAME_MS) blockedMs += Math.min(gap, GAP_CAP_MS) - LONG_FRAME_MS;
      }
      if (t - first >= FRAME_WINDOW_MS) {
        stop();
        if (framesSaySlow({ blockedMs }) && !state.lite) {
          write(storage, LITE_FRAMES_KEY, String(now()));
          apply({ lite: true, by: 'frames' }, stateWin || win);
        }
        return;
      }
      win.requestAnimationFrame(tick);
    };
    const start = () => {
      if (started || stopped) return;
      started = true;
      win.requestAnimationFrame(tick);
    };
    // A page that goes to the background decides nothing; one opened in the background starts once it is seen.
    function onVisibility() {
      if (hidden()) { if (started) stop(); }
      else start();
    }
    doc.addEventListener('visibilitychange', onVisibility);
    if (!hidden()) start();
    return stop;
  } catch (e) {
    return noop;
  }
}

/** Tests only: back to "not decided", the browser's own matchMedia back in place. */
export function __resetLite() {
  const win = stateWin || defaultWindow();
  if (win && typeof win.matchMedia === 'function' && win.matchMedia.__stylonLiteReal) win.matchMedia = win.matchMedia.__stylonLiteReal;
  const root = win && win.document && win.document.documentElement;
  if (root && typeof root.removeAttribute === 'function') root.removeAttribute('data-lite');
  state = { lite: false, by: null };
  stateWin = null;
  listeners.clear();
}
